/**
 * Positions table column registry (owner 2026-09-27). Every money value is exact integer cents
 * from `open_cost_ticks` (SPEC §8, trading/pnl.ts): the average is the rational
 * `openCostTicks / |netQty|` and P&L never divides. Default columns and their order are
 * Tradovate's Positions module defaults (docs/DESIGN-REFERENCE.md "Table columns").
 */
import { avgPrice, openPnlCents, openTickContracts } from "../trading/pnl.ts";
import type { ColumnDef, Rational, TableDef } from "./columns.ts";
import type { Excursion } from "./excursion.ts";

/** Everything one Positions row shows, assembled by the web view model. */
export type PositionFacts = {
  key: string;
  accountId: string;
  /** Alias or id (privacy masking happens at render). */
  accountLabel: string;
  symbol: string;
  /** Signed: + long, − short. */
  netQty: number;
  /** Σ price_ticks × qty over the open contracts. */
  openCostTicks: bigint;
  /** False when the gateway sent only a rounded average (P&L then approximate). */
  exactCost: boolean;
  /** Last trade (else the exit side of the BBO), ticks; null = unknown. */
  markTicks: number | null;
  /** Cents per tick per contract; null/≤0 = instrument not loaded. */
  tickValueCents: number | null;
  /** The gateway's fills-derived realized P&L for this account + contract (gross, since the gateway session started). */
  realizedCents: bigint;
  /** Commissions + exchange fees for this account + contract; null until the fee feed exists. */
  feesCents: bigint | null;
  excursion: Excursion | null;
  /** When the position was opened (epoch ms); null when the opening fill is not in view. */
  openedMs: number | null;
  nowMs: number;
  /** The account's risk-engine state (gateway `RiskState`); null until the gateway sends it. */
  risk: { roomCents: bigint; roomTicksAtPosition: bigint } | null;
  /** Contracts bought / sold in this account + contract over the fills in view (gateway session). */
  boughtQty: number;
  soldQty: number;
  /**
   * Working bracket children protecting this position (ADR-0025): contracts under a stop and under
   * a target, over how many legs; null when none.
   */
  bracket?: { stopQty: number; targetQty: number; legs: number } | null;
};

/** "SL 2 · TP 2 · 2 legs" for the Positions table's Bracket column; null without children. */
export function positionBracketText(f: Pick<PositionFacts, "bracket">): string | null {
  const b = f.bracket;
  if (!b || (b.stopQty === 0 && b.targetQty === 0)) return null;
  const parts = [`SL ${String(b.stopQty)}`, `TP ${String(b.targetQty)}`];
  if (b.legs > 1) parts.push(`${String(b.legs)} legs`);
  return parts.join(" · ");
}

export const PENDING_FEES =
  "Fee feed: commissions + exchange fees per fill (SPEC §10.7). The gateway sends 0 marked 'estimate' until fees are wired (ADR-0011 item 8).";
export const PENDING_RISK =
  "Risk engine RiskState frames from the gateway (SPEC §7.3, §10.3): room, threshold and status per account.";
const PENDING_DAY =
  "Needs the fee feed and trading-day realized P&L per contract (the gateway's position realized P&L runs since its session started, not since the 17:00 CT reset).";

/** Open P&L in cents, or null when the mark or tick value is unknown. */
export function positionOpenPnl(f: PositionFacts): bigint | null {
  if (f.markTicks === null || f.tickValueCents === null || f.tickValueCents <= 0) return null;
  return openPnlCents({ netQty: f.netQty, openCostTicks: f.openCostTicks }, f.markTicks, f.tickValueCents);
}

/** Realized + open (Tradovate "Total P/L"); null when open P&L is unknown. */
export function positionNetPnl(f: PositionFacts): bigint | null {
  const open = positionOpenPnl(f);
  return open === null ? null : f.realizedCents + open;
}

function netAfterFees(f: PositionFacts): bigint | null {
  const net = positionNetPnl(f);
  return net === null || f.feesCents === null ? null : net - f.feesCents;
}

/** Open P&L in ticks per contract as an exact fraction (tick-contracts ÷ |qty|). */
export function positionTicksPerContract(f: PositionFacts): Rational | null {
  if (f.markTicks === null || f.netQty === 0) return null;
  const tc = openTickContracts({ netQty: f.netQty, openCostTicks: f.openCostTicks }, f.markTicks);
  return { num: tc, den: BigInt(Math.abs(f.netQty)) };
}

type Col = ColumnDef<PositionFacts>;

export const POSITION_COLUMNS: readonly Col[] = [
  {
    id: "account",
    label: "Account",
    category: "Position",
    definition: "The account holding the position (alias or Rithmic account id).",
    kind: "account",
    width: 140,
    minWidth: 70,
    value: (f) => f.accountLabel,
  },
  {
    id: "symbol",
    label: "Symbol",
    category: "Position",
    definition: "The contract (Tradovate: Instrument), e.g. NQZ6. Always shown.",
    kind: "symbol",
    width: 90,
    minWidth: 60,
    locked: true,
    value: (f) => f.symbol,
  },
  {
    id: "netPos",
    label: "Net Pos",
    category: "Position",
    definition: "Signed net position in contracts: positive = long, negative = short.",
    kind: "signedQty",
    width: 70,
    minWidth: 50,
    value: (f) => f.netQty,
  },
  {
    id: "side",
    label: "Side",
    category: "Position",
    definition: "LONG or SHORT (the sign of Net Pos).",
    kind: "side",
    width: 76,
    minWidth: 60,
    value: (f) => (f.netQty > 0 ? "Long" : f.netQty < 0 ? "Short" : "Flat"),
  },
  {
    id: "size",
    label: "Size",
    category: "Position",
    definition: "Open contracts, unsigned (|Net Pos|).",
    kind: "qty",
    width: 56,
    minWidth: 44,
    value: (f) => Math.abs(f.netQty),
  },
  {
    id: "bought",
    label: "Bought",
    category: "Position",
    definition:
      "Contracts bought in this account + contract this session (Σ buy fill qty since the gateway session started).",
    kind: "qty",
    width: 64,
    minWidth: 50,
    value: (f) => f.boughtQty,
  },
  {
    id: "sold",
    label: "Sold",
    category: "Position",
    definition:
      "Contracts sold in this account + contract this session (Σ sell fill qty since the gateway session started).",
    kind: "qty",
    width: 64,
    minWidth: 50,
    value: (f) => f.soldQty,
  },
  {
    id: "avgPrice",
    label: "Net Price",
    category: "Position",
    definition:
      "Average entry price of the open contracts (Tradovate: Net Price; NinjaTrader: Avg. price; Tradesea: Average Price) = Σ fill price × qty ÷ |Net Pos|, exact. A fractional average after scaling in shows 2 extra decimals.",
    kind: "avgPrice",
    width: 96,
    minWidth: 70,
    value: (f) => avgPrice({ netQty: f.netQty, openCostTicks: f.openCostTicks }),
  },
  {
    id: "market",
    label: "Market Price",
    category: "Position",
    definition:
      "The price open P/L is marked at: the last trade (Q-U6), else the side the position would exit into (bid for a long, ask for a short).",
    kind: "price",
    width: 96,
    minWidth: 70,
    value: (f) => f.markTicks,
  },
  {
    id: "openPnl",
    label: "Open P/L",
    category: "P&L",
    definition:
      "Unrealized P/L of the open contracts at the market price: (|q| × market − Σ price × qty) × tick value for a long, the negative for a short. Exact integer cents.",
    kind: "pnl",
    width: 96,
    minWidth: 70,
    subtotal: true,
    value: positionOpenPnl,
  },
  {
    id: "ticksPnl",
    label: "P/L Ticks",
    category: "P&L",
    definition:
      "Open P/L in ticks per contract: (market − avg price) in ticks for a long, the negative for a short.",
    kind: "ticksPerContract",
    width: 72,
    minWidth: 56,
    value: positionTicksPerContract,
  },
  {
    id: "realized",
    label: "Realized P/L",
    category: "P&L",
    definition:
      "Closed P/L of this account + contract from TradePrizm's fills, gross of fees, since the gateway session started.",
    kind: "pnl",
    width: 96,
    minWidth: 70,
    subtotal: true,
    value: (f) => f.realizedCents,
  },
  {
    id: "netPnl",
    label: "Net P/L",
    category: "P&L",
    definition: "Realized P/L + Open P/L, gross of fees (Tradovate: Total P/L).",
    kind: "pnl",
    width: 96,
    minWidth: 70,
    subtotal: true,
    value: positionNetPnl,
  },
  {
    id: "fees",
    label: "Fees",
    category: "P&L",
    definition: "Commissions + exchange + NFA fees on this account + contract's fills (SPEC §10.7).",
    kind: "money",
    width: 80,
    minWidth: 60,
    subtotal: true,
    pending: PENDING_FEES,
    value: (f) => f.feesCents,
  },
  {
    id: "netAfterFees",
    label: "Net P/L after fees",
    category: "P&L",
    definition: "Net P/L − Fees.",
    kind: "pnl",
    width: 110,
    minWidth: 80,
    subtotal: true,
    pending: PENDING_FEES,
    value: netAfterFees,
  },
  {
    id: "dayPnl",
    label: "Day P/L",
    category: "P&L",
    definition:
      "This contract's share of the day P&L (SPEC §10.1 day_pnl): realized since the 17:00 CT reset − today's fees + Open P/L.",
    kind: "pnl",
    width: 90,
    minWidth: 70,
    subtotal: true,
    pending: PENDING_DAY,
    value: () => null,
  },
  {
    id: "maxDrawdown",
    label: "Max Drawdown",
    category: "Excursion",
    definition:
      "MAE: min(0, the lowest Open P/L observed since the position opened) — the worst it has been, for the whole position. Tracked by this window from the live mark; a reload restarts it from the current value. Resets when the position goes flat or flips side.",
    kind: "pnl",
    width: 100,
    minWidth: 70,
    value: (f) => f.excursion?.maeCents ?? null,
  },
  {
    id: "maxRunup",
    label: "Max Run-up",
    category: "Excursion",
    definition:
      "MFE: max(0, the highest Open P/L observed since the position opened). Same tracking rules as Max Drawdown.",
    kind: "pnl",
    width: 100,
    minWidth: 70,
    value: (f) => f.excursion?.mfeCents ?? null,
  },
  {
    id: "ddRoom",
    label: "DD Room",
    category: "Risk",
    definition:
      "The account's room to its trailing drawdown threshold (SPEC §10.3 room_cents = equity − threshold), from the gateway risk engine. Account-level: the same for every position of the account.",
    kind: "money",
    width: 96,
    minWidth: 70,
    pending: PENDING_RISK,
    value: (f) => f.risk?.roomCents ?? null,
  },
  {
    id: "roomTicks",
    label: "Room (ticks)",
    category: "Risk",
    definition:
      "Drawdown room in ticks of this position: floor(room ÷ (|Net Pos| × tick value)), from the gateway risk engine (room_ticks_at_position).",
    kind: "int",
    width: 84,
    minWidth: 60,
    pending: PENDING_RISK,
    value: (f) => (f.risk ? f.risk.roomTicksAtPosition : null),
  },
  {
    id: "opened",
    label: "Time Opened",
    category: "Time",
    definition:
      "Time of the fill that opened the position (from flat or the other side). '—' when that fill is older than this gateway session.",
    kind: "time",
    width: 120,
    minWidth: 70,
    value: (f) => f.openedMs,
  },
  {
    id: "duration",
    label: "Duration",
    category: "Time",
    definition: "How long the position has been open (now − Time Opened).",
    kind: "duration",
    width: 80,
    minWidth: 60,
    value: (f) => (f.openedMs === null ? null : Math.max(0, f.nowMs - f.openedMs)),
  },
  {
    id: "tickValue",
    label: "Tick Value",
    category: "Contract",
    definition: "Dollar value of one tick for one contract (instrument info).",
    kind: "tickValue",
    width: 76,
    minWidth: 60,
    value: (f) => (f.tickValueCents !== null && f.tickValueCents > 0 ? f.tickValueCents : null),
  },
  {
    id: "bracket",
    label: "Bracket",
    category: "Position",
    definition:
      "Contracts under a working bracket stop / target (ADR-0025), placed and kept sized by the gateway. 'SL 0' means the position has no working stop.",
    kind: "text",
    width: 130,
    minWidth: 70,
    value: (f) => positionBracketText(f),
  },
  {
    id: "exit",
    label: "Exit",
    category: "Actions",
    definition:
      "Exit at Mkt & Cxl (Tradovate): cancels this contract's working orders on the row's account, then exits the position at market. Live on the mock / Rithmic Test only (ADR-0021).",
    kind: "action",
    width: 64,
    minWidth: 56,
    unsortable: true,
    value: () => "exit",
  },
];

/**
 * Tradovate Positions module defaults (Compact view), in order (docs/DESIGN-REFERENCE.md "Table
 * columns"): Instrument (locked) · the unlabeled Long/Short cell · Net Pos · Net Price · Open P/L $.
 */
export const TRADOVATE_POSITION_COLUMNS = ["symbol", "side", "netPos", "avgPrice", "openPnl"] as const;

/**
 * HELM's Positions defaults: Tradovate's, plus the per-position Exit (Exit at Mkt & Cxl) as the
 * last column — the owner asked for a per-position exit on every row (2026-09-27, ADR-0021).
 */
export const POSITION_DEFAULTS = [...TRADOVATE_POSITION_COLUMNS, "exit"] as const;

export const POSITIONS_TABLE: TableDef<PositionFacts> = {
  id: "positions",
  label: "Positions",
  columns: POSITION_COLUMNS,
  defaults: POSITION_DEFAULTS,
  autoAccountColumn: "account",
  builtIns: [
    { id: "builtin:tradovate", name: "Tradovate default", columns: [...TRADOVATE_POSITION_COLUMNS] },
    {
      id: "builtin:ninjatrader",
      name: "NinjaTrader default",
      // NT8 Positions tab defaults: Instrument, Side, Quantity, Avg. price, PnL (unrealized),
      // Account display name (Connection has no HELM equivalent).
      columns: ["symbol", "side", "size", "avgPrice", "openPnl", "account"],
    },
    {
      id: "builtin:tradesea",
      name: "TradePrizm classic (Tradesea)",
      columns: ["symbol", "side", "size", "avgPrice", "openPnl", "exit"],
    },
    {
      id: "builtin:pnl",
      name: "Full P&L and risk",
      columns: [
        "symbol",
        "side",
        "netPos",
        "avgPrice",
        "market",
        "openPnl",
        "ticksPnl",
        "realized",
        "netPnl",
        "fees",
        "netAfterFees",
        "maxDrawdown",
        "maxRunup",
        "ddRoom",
        "opened",
        "duration",
      ],
    },
  ],
};
